import type { ReactNode } from "react";
import { motion } from "motion/react";
import { Button, Icon, cn, spot, type IconName } from "../../ui/index.tsx";
import { fmtDate } from "../../format.ts";
import { Aurora, CountUp, Springy, pop, stagger } from "../member/kit.tsx";
import { dayLabel, nextExpiry } from "./model.ts";
import type { Entitlement } from "../../../shared/types.ts";

function StatTile({ label, icon, children, hint, tone }: { label: string; icon: IconName; children: ReactNode; hint?: ReactNode; tone?: "warn" }) {
  return (
    <motion.div variants={pop} className="pane hud spotlight min-w-0 p-4" onMouseMove={spot}>
      <div className="flex items-center justify-between gap-2">
        <span className="kicker">{label}</span>
        <Icon name={icon} className={cn("size-4", tone === "warn" ? "text-warn" : "text-faint")} />
      </div>
      <div className="mt-3 font-display text-3xl font-semibold leading-tight tabular-nums tracking-tight">{children}</div>
      <div className="mt-1 min-h-4 truncate text-xs text-faint">{hint}</div>
    </motion.div>
  );
}

export function Hero({ list, onRedeem, onBrowse }: { list: Entitlement[]; onRedeem: () => void; onBrowse: () => void }) {
  const active = list.filter((e) => e.active);
  const lifetime = active.filter((e) => e.expires_at === null).length;
  const next = nextExpiry(list);
  const urgent = next !== null && next.days <= 7;

  return (
    <section className="pane hud hud-accent relative overflow-hidden p-5 sm:p-8">
      <Aurora />
      <div className="relative flex flex-wrap items-end justify-between gap-5">
        <div className="min-w-0">
          <div className="kicker mb-3 flex items-center gap-2">
            <span className="size-1.5 rounded-full bg-accent shadow-[0_0_10px_var(--color-accent)]" />
            // licenses
          </div>
          <h1 className="font-display text-4xl font-semibold uppercase leading-none tracking-tight sm:text-5xl">
            Your <span className="text-gradient">library</span>
          </h1>
          <p className="mt-3 max-w-xl text-sm text-muted">Everything you own, when it runs out, and where it came from. Redeem a key or top up any game in a couple of clicks.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Springy><Button icon="key" onClick={onRedeem}>Redeem a key</Button></Springy>
          <Springy><Button variant="primary" icon="card" onClick={onBrowse}>Buy or extend</Button></Springy>
        </div>
      </div>

      <motion.div variants={stagger(0.09, 0.15)} initial="hidden" animate="show" className="relative mt-6 grid gap-3 sm:grid-cols-3">
        <StatTile label="Games owned" icon="gamepad" hint={list.length > active.length ? `${list.length - active.length} expired` : "All active"}>
          <CountUp value={active.length} />
        </StatTile>
        <StatTile label="Lifetime licenses" icon="shield" hint={lifetime ? "Never expire" : "None yet"}>
          <CountUp value={lifetime} />
        </StatTile>
        <StatTile label="Next expiry" icon="clock" tone={urgent ? "warn" : undefined} hint={next ? fmtDate(next.entitlement.expires_at) : "Every license is current"}>
          {next ? (
            <span className="block text-xl leading-snug sm:text-2xl">
              <span className="break-words">{next.entitlement.game_name}</span>
              <span className="text-faint"> · </span>
              <span className={urgent ? "text-warn" : "text-gradient"}>{dayLabel(next.days)}</span>
            </span>
          ) : (
            <span className="block text-xl leading-snug text-muted sm:text-2xl">Nothing expiring</span>
          )}
        </StatTile>
      </motion.div>
    </section>
  );
}
